import { Unity, useUnityContext } from "react-unity-webgl";
import { useAudio } from '../../providers/AudioContext';
import {
  StyledUnityPlayer,
  LoadingScreenWrapper,
  LoadingScreenContent,
  LoadingPortraits,
  LoadingInfo,
  LoadingBarWrapper,
  LoadingBar,
  PortraitOpponent,
  PortraitMy,
  PlayerWrapper,
  TopPlayers,
  Vs,
  PlayerInfo,
  LoadingStatusWrapper,
  LoadingStatus,
  CornerLT,
  CornerLB,
  CornerRT,
  CornerRB,
  XpIcon,
  LoadingBottomContent,
  PrepareForBattle,
  LoadingSeparatorBar,
  LoadingBarText,
  LoadingLevelCaption,
  QueueStatusWrapper,
  QueueStatusContent,
  QueueStatusValue,
  QueueStatusTitle
} from './UnityPlayer.styled';
import { AuthContext } from '../../providers/AuthProvider';
import { AppContext } from '../../providers/AppContext';
import { IconButton } from '../../components';
import { useEffect, useState, useContext, useRef } from 'react';
import { DiscordSdkContext } from '../../providers/DiscordSdkProvider';
import { FigureModel } from '../../models';
import { BASEJUMP_API_URL } from '../../constants';
import { LoadingTutorial } from "../LoadingTutorial/LoadingTutorial";
import { theme } from '../../theme';

const OpponentPlayer = ({ player }) => {
  return (
    <PlayerInfo $opponent={true}>
      <div>{player.name}</div>
      <div className='xp'>
        <div>{player.xp || '0'}</div>
        <XpIcon src='/assets/points.svg'/>
      </div>
    </PlayerInfo>
  );
};

const MyPlayer = ({ player }) => {
  return (
    <PlayerInfo $opponent={false}>
      <div>{player.name}</div>
      <div className='xp'>
        <XpIcon src='/assets/points.svg'/>
        <div>{player.xp || '0'}</div>
      </div>
    </PlayerInfo>
  );
};

const Players = ({ my, opponent}) => {
  return (
    <TopPlayers>
      <MyPlayer player={my}/>
      <Vs src='/assets/vs.png'/>
      <OpponentPlayer player={opponent}/>
    </TopPlayers>
  );
};

export const LoadingScreen = ({ progress = 30, queueLabel, isLoaded, sessionReady, levelCaption, myPlayerData, opponentPlayerData, onCancel, barColor = theme.buttonGreen }) => {
   const loadingStrings = [
    'WELCOME TO BROADSIDE',
    '© 2022-2037 BY PROTA ®',
    '>ESTABLISHING PERIMETER…',
    '>RENDERING ARMOR…',
    '>ACTIVATING MAP CAMS…',
    '>CALIBRATING HAPTICS…',
    '>RENDERING MAP…',
    '>RENDERING WEAPONS…',
    '>BROADSIDE BEGINS IN 5…'
  ];
  const { playMusic } = useAudio();

  const chooseRandomString = () => {
    const randomIndex = Math.floor(Math.random() * loadingStrings.length);
    return loadingStrings[randomIndex];
  };

  useEffect(() => {
    playMusic('battle_loading');
    let interval = setInterval(() => setStatusString(chooseRandomString), 2000);
    return () => clearInterval(interval);
  }, []);

  const [statusString, setStatusString] = useState(chooseRandomString());

  return (
    <LoadingScreenWrapper style={{ opacity: isLoaded && sessionReady ? '0' : '1', display: isLoaded && sessionReady ? 'none' : 'block' }}>
      <LoadingScreenContent>
        <LoadingPortraits>
          <PortraitMy>
            <img src={myPlayerData.portrait}/>
          </PortraitMy>
          <PortraitOpponent>
            <img src={opponentPlayerData.portrait}/>
          </PortraitOpponent>
        </LoadingPortraits>
        <LoadingSeparatorBar />
        <LoadingInfo>
          <PrepareForBattle src='/assets/prepare_new.png'/>
          <Players my={myPlayerData} opponent={opponentPlayerData} />
          <LoadingBottomContent>
            <LoadingTutorial />
          </LoadingBottomContent>
        </LoadingInfo>
        <LoadingBarWrapper>
          <LoadingBar style={{ width: `${progress}%`, background: barColor }}>
            <LoadingBarText>{statusString}</LoadingBarText>
          </LoadingBar>
        </LoadingBarWrapper>
        <LoadingStatusWrapper>
          {levelCaption?.length && <LoadingLevelCaption>{levelCaption}</LoadingLevelCaption>}
        </LoadingStatusWrapper>
        {!sessionReady && <QueueStatusWrapper>
          <QueueStatusContent>
            <QueueStatusTitle>
              Your position in a queue:
            </QueueStatusTitle>
            <QueueStatusValue>
              {queueLabel}
            </QueueStatusValue>
            <LoadingStatus $ready={!!sessionReady}>
              <IconButton disabled={sessionReady} onClick={onCancel} responsive={true}>
                Cancel
              </IconButton>
            </LoadingStatus>
          </QueueStatusContent>
        </QueueStatusWrapper>}
      </LoadingScreenContent>
    </LoadingScreenWrapper>
  );
};

const SESSION_TIMEOUT_MS = 180000;

const UnityPlayer = ({ gameSessionId, myPortrait, sessionReady = false, onCancelSession, onTimeout, sessionData, onWin, onLose, onGameStarted, isGamePaused, queueData = null }) => {
  const { isDiscord } = useContext(DiscordSdkContext);
  const { user, basejumpToken } = useContext(AuthContext);
  const { musicVolume, sfxVolume, stopMusic, playSFX } = useAudio();
  const { setLoadedSessionId } = useContext(AppContext);
  const buildUrl = '/unity/Build';
  const [isGameLoaded, setIsGameLoaded] = useState(false);
  const [gameWasPaused, setGameWasPaused] = useState(false);
  const [devicePixelRatio, setDevicePixelRatio] = useState(
    window.devicePixelRatio
  );
  const timeoutRef = useRef(null);

  /*
   * Let's comment this out for now - we'll try to debug via progress bar colors
   *
  useEffect(() => {
    if (sessionReady && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => onTimeout(), SESSION_TIMEOUT_MS);
    }
  }, [sessionReady]);
  */

  useEffect(
    function () {
      const updateDevicePixelRatio = function () {
        setDevicePixelRatio(window.devicePixelRatio);
      };
      const mediaMatcher = window.matchMedia(
        `screen and (resolution: ${devicePixelRatio}dppx)`
      );
      mediaMatcher.addEventListener("change", updateDevicePixelRatio);
      return function () {
        mediaMatcher.removeEventListener("change", updateDevicePixelRatio);
      };
    },
    [devicePixelRatio]
  );
  const { unityProvider, unload, sendMessage, isLoaded, addEventListener, removeEventListener, loadingProgression } = useUnityContext({
    arguments: [],
    dataUrl: buildUrl + `/web.data?ts=${new Date().getTime()}`,
    frameworkUrl: buildUrl + `/web.framework.js?ts=${new Date().getTime()}`,
    codeUrl: buildUrl + `/web.wasm?ts=${new Date().getTime()}`,
    loaderUrl: buildUrl + `/web.loader.js?ts=${new Date().getTime()}`,
    streamingAssetsUrl: "/unity/StreamingAssets",
    companyName: "BaseJump",
    productName: "BaseJump",
    productVersion: "0.1"
  });

  useEffect(() => {
    playSFX('prepare_for_battle');
  }, []);

  useEffect(() => {
    if (isLoaded && sessionReady) {
      sendMessage(
        'PlayerData',
        'SetPlayerData',
        JSON.stringify({ apiUrl: `${BASEJUMP_API_URL}/api/v0/`, sessionId: gameSessionId, playerJwtToken: basejumpToken })
      );
    }
  }, [isLoaded, sessionReady]);

  useEffect(() => {
    const onWinEvent = () => {
      setTimeout(async () => {
        unload().then(() => {
          onWin({
            level: sessionData.level?.order
          });
        });
      }, 1000);
    };

    const onLoseEvent = () => {
      setTimeout(async () => {
        unload().then(() => {
          onLose({
            level: sessionData.level?.order
          });
        });
      }, 1000);
    };
    
    const onGameReadyEvent = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsGameLoaded(true);
      stopMusic();
      // Triger start event with info about current level
      if (onGameStarted) {
        onGameStarted({
          level: sessionData.level.order
        });
      }
    };

    addEventListener('GameReady', onGameReadyEvent);
    addEventListener('Win', onWinEvent);
    addEventListener('Lose', onLoseEvent);

    // addEventListener("GamePaused", handleGamePaused);
    // addEventListener("GameResumed", handleGameResumed);

    return () => {
      removeEventListener('GameReady', onGameReadyEvent);
      removeEventListener('Win', onWinEvent);
      removeEventListener('Lose', onLoseEvent);
    };
  }, [unload]);

  useEffect(() => {
    if (isLoaded) {
      const volume = Math.floor(sfxVolume * 100);
      sendMessage("AudioManager", "Sfx", volume);
      setLoadedSessionId(gameSessionId)
    }
  }, [isLoaded, sfxVolume, gameSessionId]);

  useEffect(() => {
    if (isLoaded) {
      const volume = Math.floor(musicVolume * 100);
      sendMessage("AudioManager", "Audio", volume);
    }
  }, [isLoaded, musicVolume]);

  useEffect(() => {
    if (isLoaded) {
      if (isGamePaused) {
        sendMessage("PauseManager", "Pause");
        setGameWasPaused(true);
      } else if (gameWasPaused) {
        sendMessage("PauseManager", "Resume");
      }
    }
  }, [isLoaded, isGamePaused]);

  const queueLabel = queueData ? `${queueData.place} of ${queueData.length}` : 'Estimating...';
  const myPlayerData = { name: user.user_name, xp: user.xp, portrait: myPortrait };
  const opponent = sessionData?.users.find(p => p.user_name !== user.user_name);
  const opponentAvatar = FigureModel.fromBasejumpAvatar(sessionData?.players.find(a => a.owner.user_name === opponent.user_name), isDiscord);
  const opponentIsNpc = !!opponent.is_npc;
  const opponentPlayerData = { name: opponentIsNpc ? opponentAvatar.getName() : opponent.user_name, xp: opponent.xp, portrait: opponentAvatar.getAvatarImgSource() };
  let barColor = theme.buttonGreen;
  if (isLoaded) {
    barColor = theme.baseOrange;
    if (isGamePaused) {
      barColor = theme.baseBlue;
    }
  }
  if (isGameLoaded) {
    if (isLoaded) {
      barColor = theme.baseRed;
      if (isGamePaused) {
        barColor = theme.baseYellow;
      }
    } else {
      barColor = theme.basePurple;
    }
  }
  return (
    <StyledUnityPlayer>
      <PlayerWrapper>
        {sessionReady && <Unity style={{ width: '100%', height: '100%' }} devicePixelRatio={devicePixelRatio} unityProvider={unityProvider} />}
        <LoadingScreen queueLabel={queueLabel} barColor={barColor} levelCaption={sessionData?.level?.loading_caption} onCancel={onCancelSession} isLoaded={isGameLoaded} sessionReady={sessionReady} myPlayerData={myPlayerData} opponentPlayerData={opponentPlayerData} progress={loadingProgression * 100}/>
        <CornerLT/>
        <CornerLB/>
        <CornerRT/>
        <CornerRB/>
      </PlayerWrapper>
    </StyledUnityPlayer>
  );
};

export default UnityPlayer;
